import {
  Progress
} from "./chunk-5A2ZTJPT.js";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "./chunk-WMZKACFO.js";
import {
  PanelCloseButton
} from "./chunk-W22Y52AU.js";
import {
  PanelMaximizeButton,
  panelDragHandle
} from "./chunk-7UHMFDJC.js";
import {
  Button
} from "./chunk-QXEA2YZ6.js";
import {
  slideFromRight,
  springSmooth
} from "./chunk-X5OIVVU7.js";
import {
  Icon
} from "./chunk-7IEFGXFA.js";
import {
  cn
} from "./chunk-SBEJSA5Q.js";

// src/components/canvas/activity-panel/activity-panel.tsx
import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var NO_TRAINING = { visible: false, modelName: "", status: "submitted", uploadProgress: 0, onMaximize: null, onDismiss: null };
function ActivityPanel({
  trainingWidget = NO_TRAINING,
  controls,
  historyActivities,
  onActivityAction,
  activities = [],
  defaultOpen = false,
  onOpenChange,
  onClose,
  onActivityClick,
  onGoToNode,
  historyTransactions = [],
  historyLoading = false,
  historyError = null,
  historyHasMore = false,
  onLoadMoreHistory,
  className
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [activeTab, setActiveTab] = useState("activity");
  const [pending, setPending] = useState(null);
  const [actionError, setActionError] = useState("");
  const handleOpenChange = (open) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };
  useEffect(() => {
    if (trainingWidget.visible) {
      setIsOpen(true);
    }
  }, [trainingWidget.visible]);
  const hasErrors = activities.some((activity) => activity.status === "error");
  const isProcessing = activities.some((activity) => activity.status === "processing");
  const allDone = activities.length > 0 && activities.every((activity) => activity.status === "success" || activity.status === "idle");
  const isTrainingActive = trainingWidget.visible && trainingWidget.status !== "submitted";
  const getStatusText = () => {
    if (hasErrors) {
      const errorActivity = activities.find((activity) => activity.status === "error");
      return errorActivity ? `Error in ${errorActivity.title}` : "Error occurred";
    }
    if (trainingWidget.visible) {
      if (trainingWidget.status === "submitted") return `Training started \u2014 ${trainingWidget.modelName}`;
      if (trainingWidget.status === "uploading") return `Uploading ${trainingWidget.modelName}...`;
      return `Preparing ${trainingWidget.modelName}...`;
    }
    if (isProcessing) {
      const processingCount = activities.filter((activity) => activity.status === "processing").length;
      return `${processingCount} ${processingCount === 1 ? "job" : "jobs"} running`;
    }
    const waiting = activities.filter((activity) => activity.status === "queued" || activity.status === "paused").length;
    return waiting ? `${waiting} ${waiting === 1 ? "job" : "jobs"} waiting` : "Activity";
  };
  const getStatusIcon = (status) => {
    switch (status) {
      case "processing":
        return /* @__PURE__ */ jsx(Icon, { name: "loader-2", className: "h-4 w-4 animate-spin text-foreground" });
      case "queued":
        return /* @__PURE__ */ jsx(Icon, { name: "clock", className: "h-4 w-4 text-muted-foreground" });
      case "paused":
        return /* @__PURE__ */ jsx(Icon, { name: "pause", className: "h-4 w-4 text-muted-foreground" });
      case "cancelled":
        return /* @__PURE__ */ jsx(Icon, { name: "square", className: "h-4 w-4 text-muted-foreground" });
      case "success":
        return /* @__PURE__ */ jsx(Icon, { name: "check", className: "h-4 w-4 text-foreground" });
      case "error":
        return /* @__PURE__ */ jsx(Icon, { name: "x", className: "h-4 w-4 text-destructive" });
      default:
        return /* @__PURE__ */ jsx(Icon, { name: "circle", className: "h-4 w-4 text-foreground" });
    }
  };
  const getNodeTypeIcon = (nodeType) => {
    switch (nodeType) {
      case "text":
        return /* @__PURE__ */ jsx(Icon, { name: "square-pen", className: "h-4 w-4 text-foreground" });
      case "image":
        return /* @__PURE__ */ jsx(Icon, { name: "image", className: "h-4 w-4 text-foreground" });
      case "video":
        return /* @__PURE__ */ jsx(Icon, { name: "video", className: "h-4 w-4 text-foreground" });
      default:
        return /* @__PURE__ */ jsx(Icon, { name: "codesandbox", className: "h-4 w-4 text-foreground" });
    }
  };
  const actionLabels = { cancel: "Cancel", retry: "Retry", pause: "Pause job", resume: "Resume job", "move-up": "Move up", "move-down": "Move down" };
  const actionIcons = { cancel: "square", retry: "rotate-ccw", pause: "pause", resume: "play", "move-up": "arrow-up", "move-down": "arrow-down" };
  const act = async (activity, action) => {
    if (!onActivityAction || pending) return;
    setPending(activity.id);
    setActionError("");
    try {
      await onActivityAction(activity, action);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Could not update this job. Please try again.");
    } finally {
      setPending(null);
    }
  };
  const renderActivity = (activity) => /* @__PURE__ */ jsxs("div", { "data-job-id": activity.id, className: "min-w-0 space-y-1 rounded-lg px-2 py-2 hover:bg-muted/30", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 items-center gap-2", children: [
      /* @__PURE__ */ jsx("span", { className: "shrink-0", children: getNodeTypeIcon(activity.nodeType) }),
      onActivityClick ? /* @__PURE__ */ jsx("button", { type: "button", title: activity.title, onClick: () => onActivityClick(activity), className: "min-w-0 flex-1 truncate text-left text-sm font-medium hover:underline", children: activity.title }) : /* @__PURE__ */ jsx("p", { title: activity.title, className: "min-w-0 flex-1 truncate text-sm font-medium", children: activity.title }),
      /* @__PURE__ */ jsx("span", { className: "shrink-0", children: getStatusIcon(activity.status) })
    ] }),
    activity.description && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: activity.description }),
    activity.progress !== void 0 && activity.status === "processing" && /* @__PURE__ */ jsx(Progress, { "aria-label": `Progress for ${activity.title}`, value: activity.progress, size: "sm" }),
    activity.details?.length ? /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-1", children: activity.details.map((detail) => /* @__PURE__ */ jsxs("span", { title: `${detail.label}: ${detail.value}`, className: "max-w-full truncate rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground", children: [
      detail.label,
      " ",
      detail.value
    ] }, detail.label)) }) : null,
    activity.errorMessage && /* @__PURE__ */ jsx("p", { className: "break-words text-xs text-destructive", children: activity.errorMessage }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
      activity.actions?.map((action) => /* @__PURE__ */ jsx(Button, { type: "button", variant: "ghost", size: "icon-xs", disabled: pending !== null || !onActivityAction, "aria-label": `${actionLabels[action]} ${activity.title}`, title: actionLabels[action], onClick: () => void act(activity, action), children: /* @__PURE__ */ jsx(Icon, { name: pending === activity.id ? "loader-2" : actionIcons[action], className: cn("h-3.5 w-3.5", pending === activity.id && "animate-spin") }) }, action)),
      activity.status === "error" && onGoToNode && /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", variant: "ghost", onClick: () => onGoToNode(activity), children: "Go to node" })
    ] })
  ] }, activity.id);
  return /* @__PURE__ */ jsx(motion.div, { "data-activity-frame": true, ...slideFromRight, transition: springSmooth, children: /* @__PURE__ */ jsxs(
    Collapsible,
    {
      role: "region",
      "aria-label": "Activity",
      "data-panel-collapsed": !isOpen,
      open: isOpen,
      onOpenChange: handleOpenChange,
      className: cn(
        "bg-popover text-popover-foreground border border-border rounded-lg w-[360px] max-w-full relative shadow-float",
        hasErrors && !isOpen && "bg-destructive/10",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs("div", { ...panelDragHandle("Activity"), className: cn(
          "flex items-center justify-between px-2 py-1 relative",
          isOpen && hasErrors && "bg-destructive/10 border-b border-border"
        ), children: [
          /* @__PURE__ */ jsx("p", { className: "text-sm font-normal text-foreground truncate grow", children: getStatusText() }),
          /* @__PURE__ */ jsx(PanelMaximizeButton, {}),
          onClose && /* @__PURE__ */ jsx(PanelCloseButton, { title: "Activity", onClose }),
          /* @__PURE__ */ jsx(CollapsibleTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
            Button,
            {
              "aria-label": `${isOpen ? "Collapse" : "Expand"} Activity`,
              variant: "ghost",
              size: "sm",
              className: "h-6 w-6 p-1 bg-muted rounded-[6px] hover:bg-accent",
              children: /* @__PURE__ */ jsx(
                Icon,
                {
                  name: isOpen ? "chevron-up" : "chevron-down",
                  className: "h-4 w-4 text-foreground"
                }
              )
            }
          ) })
        ] }),
        (activities.length > 0 || trainingWidget.visible) && /* @__PURE__ */ jsx("div", { className: cn(
          "absolute bottom-0 left-0 h-[2px] rounded-[8px] transition-all duration-500",
          hasErrors ? "bg-destructive w-full" : isTrainingActive ? "bg-foreground w-1/2 animate-pulse" : trainingWidget.visible && trainingWidget.status === "submitted" ? "bg-green-500 w-full" : allDone ? "bg-green-500 w-full" : isProcessing ? "bg-foreground w-1/2 animate-pulse" : "bg-foreground w-[31px]"
        ) }),
        /* @__PURE__ */ jsxs(CollapsibleContent, { "data-activity-content": true, children: [
          /* @__PURE__ */ jsxs("div", { className: "flex gap-1 px-2 pt-2 pb-1", children: [
            /* @__PURE__ */ jsx(
              "button",
              {
                onClick: () => setActiveTab("activity"),
                className: cn(
                  "text-xs font-medium px-2.5 py-1 rounded-[6px] transition-colors",
                  activeTab === "activity" ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                ),
                children: "Activity"
              }
            ),
            /* @__PURE__ */ jsx(
              "button",
              {
                onClick: () => setActiveTab("history"),
                className: cn(
                  "text-xs font-medium px-2.5 py-1 rounded-[6px] transition-colors",
                  activeTab === "history" ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                ),
                children: "History"
              }
            )
          ] }),
          actionError && /* @__PURE__ */ jsx("p", { role: "alert", className: "px-3 py-2 text-xs text-destructive", children: actionError }),
          activeTab === "activity" && /* @__PURE__ */ jsxs(Fragment, { children: [
            controls && /* @__PURE__ */ jsx("div", { className: "space-y-2 border-b border-border px-3 py-2", children: controls }),
            activities.length === 0 && !trainingWidget.visible ? /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center justify-center py-8 text-center", children: [
              /* @__PURE__ */ jsx(Icon, { name: "inbox", className: "text-muted-foreground h-12 w-12 mb-3" }),
              /* @__PURE__ */ jsx("p", { className: "text-muted-foreground text-sm", children: "No recent activity" })
            ] }) : /* @__PURE__ */ jsxs("div", { className: "p-2 space-y-1 max-h-[40vh] overflow-y-auto genie-scroll", children: [
              trainingWidget.visible && /* @__PURE__ */ jsxs(
                "div",
                {
                  onClick: () => trainingWidget.onMaximize?.(),
                  className: "flex flex-col gap-1.5 px-2 py-2 rounded-[8px] bg-muted/50 cursor-pointer hover:bg-muted transition-colors",
                  children: [
                    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 w-full", children: [
                      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5 flex-1 min-w-0", children: [
                        /* @__PURE__ */ jsx(Icon, { name: "sparkles", className: "h-4 w-4 text-foreground flex-shrink-0" }),
                        /* @__PURE__ */ jsxs("p", { className: "text-sm font-medium text-foreground truncate", children: [
                          trainingWidget.status === "submitted" ? "Training started" : trainingWidget.status === "uploading" ? "Uploading dataset" : "Preparing dataset",
                          /* @__PURE__ */ jsxs("span", { className: "text-muted-foreground", children: [
                            " \xB7 ",
                            trainingWidget.modelName
                          ] })
                        ] })
                      ] }),
                      /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 items-center gap-0.5", children: [
                        trainingWidget.status !== "submitted" && /* @__PURE__ */ jsx(Icon, { name: "loader-2", className: "h-4 w-4 animate-spin text-foreground" }),
                        /* @__PURE__ */ jsx(
                          "button",
                          {
                            type: "button",
                            "aria-label": "Expand training dialog",
                            onClick: (e) => {
                              e.stopPropagation();
                              trainingWidget.onMaximize?.();
                            },
                            className: "rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground",
                            children: /* @__PURE__ */ jsx(Icon, { name: "maximize-2", className: "h-3.5 w-3.5" })
                          }
                        ),
                        trainingWidget.status === "submitted" && /* @__PURE__ */ jsx(
                          "button",
                          {
                            type: "button",
                            "aria-label": "Dismiss",
                            onClick: (e) => {
                              e.stopPropagation();
                              trainingWidget.onDismiss?.();
                            },
                            className: "rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground",
                            children: /* @__PURE__ */ jsx(Icon, { name: "x", className: "h-3.5 w-3.5" })
                          }
                        )
                      ] })
                    ] }),
                    trainingWidget.status === "uploading" && /* @__PURE__ */ jsx(Progress, { value: trainingWidget.uploadProgress, size: "sm" })
                  ]
                }
              ),
              activities.map(renderActivity)
            ] })
          ] }),
          activeTab === "history" && /* @__PURE__ */ jsx("div", { className: "p-2 max-h-[40vh] overflow-y-auto genie-scroll", children: historyActivities ? historyActivities.length ? /* @__PURE__ */ jsx("div", { className: "space-y-1", children: historyActivities.map(renderActivity) }) : /* @__PURE__ */ jsx("p", { className: "py-8 text-center text-sm text-muted-foreground", children: "No completed jobs yet" }) : historyLoading && historyTransactions.length === 0 ? /* @__PURE__ */ jsx("div", { className: "space-y-2", children: Array.from({ length: 4 }).map((_, i) => /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 px-2 py-1.5 animate-pulse", children: [
            /* @__PURE__ */ jsx("div", { className: "h-4 w-4 rounded-full bg-muted" }),
            /* @__PURE__ */ jsx("div", { className: "flex-1 space-y-1", children: /* @__PURE__ */ jsx("div", { className: "h-3 w-20 bg-muted rounded" }) }),
            /* @__PURE__ */ jsx("div", { className: "h-3 w-12 bg-muted rounded" })
          ] }, i)) }) : historyError ? /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center justify-center py-8 text-center", children: [
            /* @__PURE__ */ jsx(Icon, { name: "alert-circle", className: "text-destructive h-8 w-8 mb-2" }),
            /* @__PURE__ */ jsx("p", { className: "text-destructive text-sm mb-3", children: historyError }),
            /* @__PURE__ */ jsx(
              "button",
              {
                onClick: onLoadMoreHistory,
                className: "bg-muted text-foreground text-xs font-medium px-3 py-2 rounded-[8px] h-8 hover:bg-accent",
                children: "Retry"
              }
            )
          ] }) : historyTransactions.length === 0 ? /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center justify-center py-8 text-center", children: [
            /* @__PURE__ */ jsx(Icon, { name: "receipt", className: "text-muted-foreground h-12 w-12 mb-3" }),
            /* @__PURE__ */ jsx("p", { className: "text-muted-foreground text-sm", children: "No transaction history" })
          ] }) : /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
            historyTransactions.map((tx) => /* @__PURE__ */ jsx(TransactionRow, { transaction: tx }, tx.id)),
            historyHasMore && /* @__PURE__ */ jsx(
              "button",
              {
                onClick: onLoadMoreHistory,
                disabled: historyLoading,
                className: cn(
                  "w-full text-xs font-medium text-muted-foreground py-2 rounded-[8px] hover:bg-muted/50 transition-colors",
                  historyLoading && "opacity-50 cursor-not-allowed"
                ),
                children: historyLoading ? "Loading..." : "Load more"
              }
            )
          ] }) })
        ] })
      ]
    }
  ) });
}
function formatTimestamp(date) {
  const now = /* @__PURE__ */ new Date();
  const diff = now.getTime() - date.getTime();
  const seconds = Math.floor(diff / 1e3);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 1) return `${days}d ago`;
  if (days === 1) return "yesterday";
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return "Just now";
}
var TRANSACTION_META = {
  JOB_DEDUCTION: { icon: "minus-circle", label: "Generation" },
  TOP_UP: { icon: "plus-circle", label: "Top Up" },
  REFUND: { icon: "rotate-ccw", label: "Refund" },
  SUBSCRIPTION_CREDIT: { icon: "star", label: "Subscription" },
  TRAINING_HOLD: { icon: "minus-circle", label: "Training" },
  TRAINING_CONFIRMED: { icon: "check-circle", label: "Training confirmed" },
  TRAINING_REFUND: { icon: "rotate-ccw", label: "Training refund" },
  ADMIN_DEBIT: { icon: "minus-circle", label: "Adjustment" },
  REFERRAL_CREDIT: { icon: "plus-circle", label: "Invite earnings" }
};
var DEBIT_TYPES = /* @__PURE__ */ new Set(["JOB_DEDUCTION", "TRAINING_HOLD", "ADMIN_DEBIT"]);
function formatAmount(amount, type) {
  const num = parseFloat(amount);
  const abs = Math.abs(num);
  const isNegative = DEBIT_TYPES.has(type);
  const formatted = abs < 1 && abs > 0 ? `${Math.round(abs * 100)}\xA2` : `$${abs.toFixed(2)}`;
  return {
    text: `${isNegative ? "-" : "+"}${formatted}`,
    isNegative
  };
}
function TransactionRow({ transaction }) {
  const meta = TRANSACTION_META[transaction.type];
  const { text, isNegative } = formatAmount(transaction.amount, transaction.type);
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 px-2 py-1.5 rounded-[8px]", children: [
    /* @__PURE__ */ jsx(
      Icon,
      {
        name: meta.icon,
        className: cn("h-4 w-4 flex-shrink-0", isNegative ? "text-muted-foreground" : "text-emerald-400")
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "flex-1 min-w-0", children: /* @__PURE__ */ jsx("p", { className: "text-sm font-medium text-foreground truncate", children: transaction.description || meta.label }) }),
    /* @__PURE__ */ jsx(
      "span",
      {
        className: cn(
          "text-sm font-mono tabular-nums flex-shrink-0",
          isNegative ? "text-muted-foreground" : "text-emerald-400"
        ),
        children: text
      }
    ),
    /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground flex-shrink-0 w-14 text-right", children: formatTimestamp(new Date(transaction.createdAt)) })
  ] });
}

export {
  ActivityPanel
};
//# sourceMappingURL=chunk-QDVO26IK.js.map